/**
 * The geocoder implementation. Creates a new instance of service call.
 *
 * Parameters can be passed on creation or provided via convenient functions that can be chained.
 *
 * @class geocode
 */
define('services/geocode',["rest/geocode", "model/geocode/geocode", "./chain", "./validators", "./converters", "./worldEdge"],
    function (geocode, model, chain, validators, converters, worldEdge) {
        "use strict";

        var fields = {
            /**
             * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
             * tomtom.key
             * or tomtom.searchKey function.
             * But also per service instance on initialization or with this method.
             *
             * The API Key can be issued from http://developer.tomtom.com
             *
             * @method key
             * @chainable
             * @param {String} [key] key API Key valid for requested service.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|String} Method returns the service instance allowing chaining or _key_ value if
             *     argument is omitted.
             */
            key: {validator: validators.string},
            /**
             * A setter and getter for _'query'_ attribute.
             *
             * @method query
             * @chainable
             * @param {String} [query] Query string. Must be properly URL encoded.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|String} Method returns the service instance allowing chaining or _query_ value if
             *     argument is omitted.
             */
            query: {validator: validators.string, required: true},
            /**
             * A setter and getter for 'storeResult' attribute. If the _storeResult_ flag is set, the query will be
             * interpreted as a stored geocode and will be billed according to the terms of use. Default value is false
             *
             * @method storeResult
             * @chainable
             * @param {Boolean} [storeResult=false] Determines whether geocode service should be interpreted as a
             *     stored geocode.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {geocode|Boolean} Method returns the service instance allowing chaining or _storeResult_ value
             *     if argument is omitted.
             */
            storeResult: {validator: validators.bool},
            /**
             * A setter and getter for 'typeahead' attribute. If the _typeahead_ flag is set, the query will be
             * interpreted as a partial input and the search will enter predictive mode. Default value is false
             *
             * @method typeahead
             * @chainable
             * @param {Boolean} [typeahead=false] Defines whether geocode service should enter _predictive_ mode.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|Boolean} Method returns the service instance allowing chaining or _typeahead_ value if
             *     argument is omitted.
             */
            typeahead: {validator: validators.bool},
            /**
             * A setter and getter for _'limit'_ attribute. Maximum number of responses that will be returned.
             * Default value is 10.
             *
             * @method limit
             * @chainable
             * @param {integer} [limit=10] Maximum number of responses that will be returned.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|integer} Method returns the service instance allowing chaining or _limit_ value if
             *     argument is omitted.
             */
            limit: {validator: validators.positiveInteger},
            /**
             * A setter and getter for _'offset'_ attribute. Starting offset of the returned results within the full
             * result set. Default value is 0.
             *
             * @method offset
             * @chainable
             * @param {integer} [offset=0] Starting offset of the returned results within the full result set.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|integer} Method returns the service instance allowing chaining or _offset_ value
             * if argument is omitted.
             */
            offset: {validator: validators.naturalInteger, name: "ofs"},
            /**
             * A setter and getter for _'boundingBox'_ attribute. The bounding box of the area which user
             * wants to search on. It can be omitted, then whole world is taken into consideration.
             *
             * Bounding box can exceed world boundaries (-180, -90, 180, 90) it is not an issue. The service will
             * internally normalize it by overwriting any exceeding value with maximal or minimal allowed.
             * Longitude will be recalculated to fit the allowed range, if bounding box includes edge of the world
             * it will be split into two bounding boxes and the response will contain merged results for both areas.
             *
             * The service is able to convert a number of popular formats into the bounding box.
             * Supported formats are listed below:
             * * _{minLon: 0, minLat: 0, maxLon: 1, maxLat: 1}_ // plain object with minLon, minLat, maxLon, maxLat
             * fields
             * * _L.latLngBounds(...)_ // leaflet bounds object
             * * _[0, 0, 1, 1]_ // array of numbers containing in order minLon, minLat, maxLon, maxLat
             * * _ol.Extent_ // open layers extend object
             * * _[ol.Coordinate, ol.Coordinate]_ // array of two open layers coordinate objects in order southWest,
             * northEast
             * * _[[0, 0], [1, 1]]_ // array of two arrays (southWest, northEast) with latitude and longitude values
             * * _"0,0,1,1"_ // comma seperated string of number values minLon, minLat, maxLon, maxLat
             * * _[{lon: 0, lat: 0},{lon: 1, lat: 1}]_ // array of two plain objects (southWest, northEast)
             * with lat and lon fields
             * * _[{lng: 0, lat: 0},{lng: 1, lat: 1}]_ // array of two plain objects (southWest, northEast)
             * with lat and lng fields
             * * _[L.latLng(...), L.latLng(...)]_ // array of southWest and northEast leaflet latLng objects
             * * _google.maps.LatLngBounds_ // Google Maps bounds object
             * * _google.maps.LatLngBoundsLiteral_ // Google Maps bounds object
             * * _[LatLng, LatLng]_ // array of two google.maps.LatLng
             * * _[LatLngLiteral, LatLngLiteral]_ // array of two google.maps.LatLngLiteral
             *
             * @method boundingBox
             * @chainable
             * @param {Object|Array|String} [boundingBox] The bounding box of the area that search is supposed to
             * search on.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {search|String} Method returns the service instance allowing chaining or converted bounding box
             * value if argument is omitted.
             */
            boundingBox: {validator: validators.boundingBox, converter: converters.boundingBox},
            /**
             * A setter and getter for _'center'_ attribute. This attribute allows to bias the search results.
             *
             * Supplying only that attribute - without _radius_ parameter - will bias search results only to
             * that area.
             *
             * The service is able to convert a number of popular formats into the point.
             * Supported formats are listed below:
             * * _[0, 0]_  //  array of latitude and longitude
             * * _"0,1"_  //  string of latitude and longitude, divided by comma
             * * _L.latLngBounds(...)_  //  leaflet bounds object
             * * _{lat: function() {...}, lng: function() {...}}_  //  object with two functions returning latitude and
             * longitude
             * * _{lat: 0, lon: 1}_    //  object with two properties: latitude and longitude
             * * _{x: 0, y: 1}_   //   object with two properties: x as latitude and y as longitude
             * * _{latitude: 0, longitude: 1}_   //   object with two properties: latitude and longitude
             *
             * @method center
             * @chainable
             * @param {Point} [center] Point with latitude and longitude data.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {search|Point} Method returns the service instance allowing chaining or _center_ value
             * if argument is omitted.
             */
            center: {converter: converters.point, validator: validators.point},
            /**
             * A setter and getter for _'countrySet'_ attribute.
             * Comma separated string of country codes (e.g.: FR,ES). This will limit the search to the specified
             * countries.
             *
             * @method countrySet
             * @chainable
             * @param {String} [countrySet] List of country codes, divided by commas.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|String} Method returns the service instance allowing chaining or _countrySet_ value
             * if argument is omitted.
             */
            countrySet: {validator: validators.countrySet},
            /**
             * A setter and getter for _'radius'_ attribute.
             * If radius and position are set, the results will be constrained to the defined area. The radius parameter
             * is specified in meters.
             *
             * @method radius
             * @chainable
             * @param {integer} [radius] Radius value specified in meters.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|integer} Method returns the service instance allowing chaining or _radius_ value
             * if argument is omitted.
             */
            radius: {validator: validators.naturalInteger},
            /**
             * A setter and getter for _'best result'_ option. It makes the service return only one, best match result.
             * This option overwrites both limit and offset parameters. It changes the response from array to single
             * result. Default value is false.
             *
             * @method bestResult
             * @chainable
             * @param {Boolean} [bestResult] Defines whether geocode service should return the best result.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|Boolean} Method returns the service instance allowing chaining or _bestResult_ value
             * if argument is omitted.
             */
            bestResult: {validator: validators.bool},
            /**
             * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] Protocol type, one of _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {geocode|String} Method returns the service instance allowing chaining or _protocol_
             * value if argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(["http", "https"], "protocol")},
            baseUrl: {validator: validators.string},
            serviceUrl: {validator: validators.string}
        };

        /**
         * The function performing actual call to the web service. This method can be executed multiple times, it is
         * not changing parameters itself.
         *
         * Callback function takes a response parameter and fail callback takes error as only parameter.
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.searchKey(<Your API key>);
         * var geocode = tomtom.geocode().query('Amsterdam').go(myFunction);
         * ```
         */
        /**
         * A setter and getter for callback function. This function is called after receiving successful response from
         * service. It takes one argument which is the response - JSON described in go() function documentation.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} if the given argument is not a function
         * @return {geocode} Method returns the service instance allowing chaining or function if argument is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {geocode|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        function addBoundingBoxParameters(data, boundingBox) {
            data.topLeft = [boundingBox.maxLat, boundingBox.minLon];
            data.btmRight = [boundingBox.minLat, boundingBox.maxLon];
            return data;
        }

        function callbackBestResult(bestResult, callback) {
            return function (response) {
                callback(bestResult ? response[0] : response);
            };
        }

        function handleChain(data, callback) {
            var bestResult = !!data.bestResult;
            if (bestResult) {
                data.limit = 1;
                data.offset = 0;
            }
            data.bestResult = null;
            if (data.center) {
                data.center = data.center.split(",");
                data.lat = data.center[0];
                data.lon = data.center[1];
                delete data.center;
            }
            worldEdge(geocode.geocode, model, data, callbackBestResult(bestResult, callback), addBoundingBoxParameters);
        }

        /**
         *
         * @method geocode
         *
         * @param {Object} [options] Options to be passed to the geocode call.
         * @param {String} [options.key] The API Key issued from <a
         *     href="http://developer.tomtom.com">http://developer.tomtom.com</a>.
         * @param {String} [options.query] Query string. Must be properly URL encoded.
         * @param {Boolean} [options.storeResult]  If the "storeResult" flag is set, the query will be interpreted as a
         *     stored geocode and will be billed according to the terms of use.
         * @param {Boolean} [options.typeahead] If the "typeahead" flag is set, the query will be interpreted as a
         *     partial input and the search will enter predictive mode.
         * @param {Integer} [options.limit] Maximum number of responses that will be returned.
         * @param {Integer} [options.offset] Starting offset of the returned results within the full result set.
         * @param {Point} [options.center] Point of center where results should be biased.
         * @param {String} [options.countrySet] Comma separated string of country codes (e.g.: FR,ES). This will limit
         *     the search to the specified countries.
         * @param {Integer} [options.radius] If radius and position are set, the results will be constrained to the
         *     defined area. The radius parameter is specified in meters.
         * @param {Object|Array|String} [options.boundingBox] Area (two points with latitude and longitude) that search
         *                  will look for results onto.
         * @param {Boolean} [options.bestResult] If the "bestResult" flag is set, service responses with single (best
         *                  match) result.
         * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
         * @param {Function} [options.fail] The callback function to be called on failure.
         * @param {Function} [options.callback] Specifies the callback method.
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * tomtom.geocoder.key(<Your API key>); // this is optional if you passed global key earlier
         * var geocoder = tomtom.geocode()
         *   .query('Amsterdam, The Netherlands')
         *   .callback(myFunction).go();
         * ```
         */
        return function (options) {
            return chain(fields, handleChain, options);
        };
    });
